// Servicio del historial de partes (spec historial-de-partes §4.4). IO
// PostgREST, sin React. Mismo patrón que `clientes.ts`: policy SELECT sobre la
// réplica, nada de RPC ni de la Edge Function del bot.

import { supabase } from '../lib/supabase';
import type { ParteReplicaRow, RevisionReplicaRow } from '../types/clientes';

// La puente viaja anidada para poder MARCAR los de la puerta de la llamada sin
// una segunda consulta (y sin filtrar por equipo: ver D2).
// `id_odoo` y `linea_negocio` entran con el change 001: el primero es el número
// de parte que la oficina ve en Odoo ("parte 4665"), y el segundo es el eje del
// modo sin puerta. La columna existe en la réplica desde el 3-sep y hasta ahora
// se escribía sin leerse.
const COLUMNAS =
  'id, id_odoo, titulo, descripcion, etapa, proyecto, linea_negocio, fecha, equipos:partes_equipos_replica(id_equipo)';

interface FilaCruda extends Omit<ParteReplicaRow, 'ids_equipos'> {
  equipos: { id_equipo: string }[] | null;
}

// Los partes activos de un cliente, recientes primero. Se traen todos y el
// panel enseña cinco (P2): un cliente con mucho historial cabe de sobra en una
// respuesta, y el "ver todos" no tiene que volver a la red.
//
// Filtra por `activo` al revés que la ficha del cliente, que se ve archivada y
// todo: un parte archivado en Odoo lo está por algo (D1), y meterlo en el
// recuento ensuciaría la respuesta al cliente.
export async function fetchPartesCliente(idCliente: string): Promise<ParteReplicaRow[]> {
  const { data, error } = await supabase
    .from('partes_replica')
    .select(COLUMNAS)
    .eq('id_cliente', idCliente)
    .eq('activo', true)
    // `nullsFirst: false` explícito: en Postgres un ORDER BY ... DESC pone los
    // NULL PRIMERO, así que un parte sin fecha encabezaría el historial. Hoy no
    // hay ninguno (create_date siempre viene), o sea que es latente y por eso
    // ningún test lo habría cazado.
    .order('fecha', { ascending: false, nullsFirst: false })
    // Desempate explícito: sin él, dos partes con la MISMA fecha vuelven en
    // orden indeterminado, y como el agrupado por línea solo enseña tres por
    // grupo, la fila visible y el «Último:» cambian entre recargas sin que nada
    // haya cambiado. Hallazgo del gate.
    .order('id', { ascending: true });

  if (error) {
    console.error(
      JSON.stringify({ evt: 'fetch_partes_failed', code: error.code, message: error.message })
    );
    throw new Error(`No pude cargar el historial de partes: ${error.message}`);
  }

  return ((data ?? []) as unknown as FilaCruda[]).map(({ equipos, ...p }) => ({
    ...p,
    ids_equipos: (equipos ?? []).map((e) => e.id_equipo),
  }));
}

// Las revisiones de UNA puerta, recientes primero (contrato §4.3 de
// revisiones-de-la-puerta). Cuelgan del equipo, así que no hay cliente que
// filtrar: el equipo ya sabe de quién es.
//
// Se traen todas las de esa puerta —el máximo medido en producción es 27, media
// 4,37— y la pantalla decide cuántas enseña, igual que con los partes.
export async function fetchRevisionesEquipo(idEquipo: string): Promise<RevisionReplicaRow[]> {
  const { data, error } = await supabase
    .from('revisiones_replica')
    .select('id, observaciones, linea_negocio, estado, fecha')
    .eq('id_equipo', idEquipo)
    // Igual que los partes: una revisión archivada en Odoo lo está por algo.
    .eq('activo', true)
    // `nullsFirst: false` explícito y por la misma razón que en los partes: en
    // Postgres un ORDER BY ... DESC pone los NULL PRIMERO, así que una revisión
    // sin fecha encabezaría el historial haciéndose pasar por la más reciente.
    // Hoy no hay ninguna (0 de 18.741 medidas el 9-sep): es latente, y este es
    // el mismo bug que ya llegó a pasar con los partes (hallazgo M3 del verify).
    .order('fecha', { ascending: false, nullsFirst: false })
    // Mismo desempate, misma razón: el bloque de la puerta corta a seis.
    .order('id', { ascending: true });

  if (error) {
    console.error(
      JSON.stringify({ evt: 'fetch_revisiones_failed', code: error.code, message: error.message })
    );
    // Lanza, no devuelve []: el panel tiene que poder distinguir «esta puerta no
    // tiene revisiones» de «no he podido consultarlas», que es el incumplimiento
    // I1 de la feature base y no se reintroduce aquí.
    throw new Error(`No pude cargar las revisiones de la puerta: ${error.message}`);
  }

  return (data ?? []) as RevisionReplicaRow[];
}
